import React, { useState, useEffect } from 'react';
import { Gauge, AlertTriangle, Loader } from 'lucide-react';
import './SendingSettings.css';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { getSendingRampPolicy, updateSendingRampPolicy, SendingRampPolicy } from '../../services/api';

export default function SendingThrottleSettings() {
    const [policy, setPolicy] = useState<SendingRampPolicy | null>(null);
    const [draft, setDraft] = useState<Partial<SendingRampPolicy>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getSendingRampPolicy()
            .then(p => { setPolicy(p); setDraft({}); })
            .catch(() => setError('Could not load ramp policy'))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const updated = await updateSendingRampPolicy(draft);
            setPolicy(updated);
            setDraft({});
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e: any) {
            setError(e.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const get = (key: keyof SendingRampPolicy): number =>
        (draft[key] as number) ?? (policy?.[key] as number) ?? 0;

    const set = (key: keyof SendingRampPolicy, value: number) =>
        setDraft(d => ({ ...d, [key]: value }));

    const numField = (label: string, key: keyof SendingRampPolicy, min: number, hint: string) => (
        <div className="throttle-field">
            <label className="sending-label">{label}</label>
            <input
                className="sending-input"
                type="number"
                min={min}
                value={get(key)}
                onChange={e => set(key, parseInt(e.target.value) || min)}
            />
            <span className="throttle-hint">{hint}</span>
        </div>
    );

    if (loading) return (
        <div className="sending-settings">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)' }}>
                <Loader size={16} className="spin" /> Loading…
            </div>
        </div>
    );

    const currentLimit = policy
        ? Math.min(
            policy.initial_batch_size + policy.current_batch_level * policy.batch_increment,
            policy.max_batch_size,
          )
        : 0;

    return (
        <div className="sending-settings">
            <div className="sending-header">
                <Gauge size={20} style={{ color: 'var(--primary)' }} />
                <div>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Sending Throttle</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                        Ramp-up settings protect your sender reputation when sending to large lists.
                        ISPs flag sudden spikes in send volume — gradual ramp-up builds trust.
                    </p>
                </div>
            </div>

            {policy && (
                <GlassCard>
                    <div style={{ padding: '4px 0 12px', borderBottom: '1px solid var(--stroke)', marginBottom: 16 }}>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Current ramp status</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginTop: 4 }}>
                            Batch level {policy.current_batch_level} —{' '}
                            <span style={{ color: 'var(--primary)' }}>{currentLimit} emails / {policy.batch_interval_minutes} min</span>
                        </div>
                        {policy.last_batch_at && (
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                                Last batch: {new Date(policy.last_batch_at).toLocaleString()}
                            </div>
                        )}
                    </div>

                    <div className="sending-warn-banner" style={{ marginBottom: 16 }}>
                        <AlertTriangle size={14} />
                        <span>Changing these values resets your ramp to level 0. Only change if you know what you're doing.</span>
                    </div>

                    <div className="throttle-grid">
                        {numField('Initial batch size', 'initial_batch_size', 1, 'Emails sent in the first batch')}
                        {numField('Batch increment', 'batch_increment', 1, 'Additional emails allowed each batch')}
                        {numField('Batch interval (min)', 'batch_interval_minutes', 5, 'Minutes between batches')}
                        {numField('Max batch size', 'max_batch_size', 1, 'Cap per interval window')}
                        {numField('Max per domain/min', 'max_per_domain_per_minute', 1, 'e.g. max 20 emails/min to gmail.com')}
                        {numField('Cooldown (days)', 'cooldown_days', 1, 'Inactivity before ramp resets to level 0')}
                    </div>
                </GlassCard>
            )}

            {error && (
                <div className="sending-error">
                    <AlertTriangle size={14} /> {error}
                </div>
            )}

            <div className="sending-actions">
                <GlassButton
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving || Object.keys(draft).length === 0}
                >
                    {saving ? <Loader size={14} className="spin" /> : null}
                    {saved ? 'Saved!' : saving ? 'Saving…' : 'Save Throttle Settings'}
                </GlassButton>
            </div>
        </div>
    );
}
